import { useCallback, useEffect, useState } from "react";

import { annotationRequest, type ComposerSelection } from "./annotation-composer-request.js";
import { readerErrorMessage } from "./errors.js";

import type {
  Annotation,
  AnnotationCreationRequest,
  AnnotationId,
  Source,
} from "@mdbase-reader/core";
import type { ReadingSurface } from "@mdbase-reader/reading-surface";

export type { ComposerSelection } from "./annotation-composer-request.js";

export interface AnnotationComposerController {
  readonly selection: ComposerSelection | null;
  readonly note: string;
  readonly status: "idle" | "saving";
  readonly error: string | null;
  readonly canSelectArea: boolean;
  readonly selectingArea: boolean;
  readonly activeAnnotationId: AnnotationId | null;
  readonly setNote: (note: string) => void;
  readonly dismiss: () => void;
  readonly save: () => void;
  readonly toggleAreaSelection: () => void;
  readonly open: (annotation: Annotation) => void;
}

interface SelectedDraft {
  readonly sourceId: string;
  readonly surface: ReadingSurface;
  readonly value: ComposerSelection;
}

export function useAnnotationComposer(input: {
  readonly source: Source | null;
  readonly surface: ReadingSurface | null;
  readonly create: (request: AnnotationCreationRequest) => Promise<Annotation>;
}): AnnotationComposerController {
  const { source, surface, create } = input;
  const [selected, setSelected] = useState<SelectedDraft | null>(null);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState<"idle" | "saving">("idle");
  const [areaSelectionSurface, setAreaSelectionSurface] = useState<ReadingSurface | null>(null);
  const [activeAnnotationId, setActiveAnnotationId] = useState<AnnotationId | null>(null);
  const [problem, setProblem] = useState<{ sourceId: string; message: string } | null>(null);
  const sourceId = source?.id;
  const selection =
    sourceId && selected?.sourceId === sourceId && selected.surface === surface
      ? selected.value
      : null;
  const error = sourceId && problem?.sourceId === sourceId ? problem.message : null;

  useEffect(
    () =>
      subscribeToSelections(
        source,
        surface,
        setSelected,
        setNote,
        setProblem,
        setAreaSelectionSurface,
        setActiveAnnotationId,
      ),
    [source, surface],
  );

  const dismiss = useCallback((): void => {
    surface?.capabilities.textSelection?.clearSelection();
    surface?.capabilities.areaSelection?.cancelAreaSelection();
    setSelected(null);
    setNote("");
    setProblem(null);
    setAreaSelectionSurface(null);
    setActiveAnnotationId(null);
  }, [surface]);

  const save = useCallback((): void => {
    if (!selection || !source || !surface || status === "saving") {
      return;
    }
    setStatus("saving");
    setProblem(null);
    void saveSelection(
      { source, surface, create },
      selection,
      note,
      dismiss,
      setProblem,
      setStatus,
    );
  }, [create, dismiss, note, selection, source, status, surface]);

  const toggleAreaSelection = useCallback((): void => {
    const capability = surface?.capabilities.areaSelection;
    if (!capability) {
      return;
    }
    if (areaSelectionSurface === surface) {
      capability.cancelAreaSelection();
      setAreaSelectionSurface(null);
      return;
    }
    surface.capabilities.textSelection?.clearSelection();
    setSelected(null);
    setProblem(null);
    capability.beginAreaSelection();
    setAreaSelectionSurface(surface);
  }, [areaSelectionSurface, surface]);

  const open = useCallback(
    (annotation: Annotation): void => {
      if (openAnnotation(annotation, { source, surface }, setProblem)) {
        setActiveAnnotationId(annotation.id);
      }
    },
    [source, surface],
  );

  return {
    selection,
    note,
    status,
    error,
    canSelectArea: Boolean(surface?.capabilities.areaSelection),
    selectingArea: areaSelectionSurface === surface,
    activeAnnotationId,
    setNote,
    dismiss,
    save,
    toggleAreaSelection,
    open,
  };
}

function subscribeToSelections(
  source: Source | null,
  surface: ReadingSurface | null,
  setSelected: (value: SelectedDraft | null) => void,
  setNote: (value: string) => void,
  setProblem: (value: null) => void,
  setAreaSelectionSurface: (value: ReadingSurface | null) => void,
  setActiveAnnotationId: (value: AnnotationId | null) => void,
): (() => void) | undefined {
  if (!source || !surface) {
    return undefined;
  }
  const select = (value: ComposerSelection): void => {
    setSelected({ sourceId: source.id, surface, value });
    setNote("");
    setProblem(null);
    setAreaSelectionSurface(null);
    setActiveAnnotationId(null);
  };
  const text = surface.capabilities.textSelection?.selections.subscribe((value) =>
    select({ kind: "text", value }),
  );
  const area = surface.capabilities.areaSelection?.selections.subscribe((value) =>
    select({ kind: "area", value }),
  );
  return () => {
    text?.();
    area?.();
  };
}

async function saveSelection(
  input: {
    readonly source: Source;
    readonly surface: ReadingSurface;
    readonly create: (request: AnnotationCreationRequest) => Promise<Annotation>;
  },
  selection: ComposerSelection,
  note: string,
  dismiss: () => void,
  setProblem: (value: { sourceId: string; message: string }) => void,
  setStatus: (value: "idle" | "saving") => void,
): Promise<void> {
  const sourceId = input.source.id;
  dismiss();
  try {
    await input.create(await annotationRequest(input.source, input.surface, selection, note));
  } catch (reason) {
    setProblem({
      sourceId,
      message: readerErrorMessage(reason, "Reader could not save this annotation."),
    });
  } finally {
    setStatus("idle");
  }
}

function openAnnotation(
  annotation: Annotation,
  input: { readonly source: Source | null; readonly surface: ReadingSurface | null },
  setProblem: (value: { sourceId: string; message: string }) => void,
): boolean {
  const surface = input.surface;
  if (!surface) {
    return false;
  }
  if (
    annotation.document &&
    (annotation.document.fileId !== surface.document.document.fileId ||
      annotation.document.revision !== surface.document.document.revision)
  ) {
    if (input.source) {
      setProblem({
        sourceId: input.source.id,
        message: "This annotation targets a different document revision and must be re-anchored.",
      });
    }
    return false;
  }
  if (annotation.target?.pdf) {
    void surface.goTo({ kind: "pdf", pageIndex: annotation.target.pdf.pageIndex });
  } else if (annotation.target?.epub) {
    void surface.goTo({
      kind: "epub",
      locator: {
        type: "application/xhtml+xml",
        locations: { fragments: [annotation.target.epub.cfi] },
      },
    });
  } else if (annotation.target?.html) {
    void surface.capabilities.annotationNavigation?.goToAnnotation(annotation);
  } else {
    return false;
  }
  return true;
}
